import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { notification } from "../../utils/Notifications";
import {
  getGuidesInMatriz,
  getUnassignedRoutes,
  getGuidesInMatrizBySuperadmin,
  getUnassignedRoutesBySuperadmin,
  getGuidesInIncidence,
  getGuidesInIncidenceBySuperadmin,
} from "../../api/guidesApi";
import { getZones } from "../../api/zonesApi";
import { getDeliveryMen, getDeliveryMenBySuperadmin } from "../../api/userApi";
import Title from "../../components/SharedComponents/Title";
import SearchBar from "../../components/SharedComponents/SearchBar";
import GuidesAssingUserDialog from "../../components/Guides/GuidesAssingUserDialog";
import useLogout from "../../hooks/auth/authHook.js";
import styles from "./guidesStyles.js";
import { UserTag } from "iconsax-react";
import GuidesTabBar from "../../components/Guides/GuidesTabBar/index.js";
import { usePermissions } from "../../hooks/permissions/usePermissions.js";

const Guides = () => {
  const [logout] = useLogout();
  const { hasAllPermissions } = usePermissions();
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const [guides, setGuides] = useState([]);
  const [unassignedGuides, setUnassignedGuides] = useState([]);
  const [guidesInIncidence, setGuidesInIncidence] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(() => {
    if (extraInformation?.id_role === 5) {
      return 1; // SuperAdmin: siempre 1 (GDL)
    } else if (extraInformation?.id_role === 1) {
      // Admin: obtener de available_zones
      const availableZones = extraInformation?.available_zones;
      if (Array.isArray(availableZones) && availableZones.length > 0) {
        return availableZones[0];
      }
      return 1; // Fallback a 1 si no hay zona disponible
    }
    return 1; // Fallback general
  });
  const [deliverers, setDeliverers] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasItemsSelected, setHasItemsSelected] = useState(false);
  const [selectedGuides, setSelectedGuides] = useState([]);
  const [openDialog, setOpenDialog] = useState(null);
  const hasFetchedInitial = useRef(false);
  const hasFetchedDeliverersInitial = useRef(false);

  const handleDialogOpen = (dialogName) => {
    setOpenDialog(dialogName);
  };

  const handleDialogClose = () => {
    setOpenDialog(null);
  };

  const fetchGuides = () => {
    setIsLoading(true);

    const fetchFunction = isSuperAdmin
      ? getGuidesInMatrizBySuperadmin
      : getGuidesInMatriz;
    const fetchParams = isSuperAdmin
      ? [Number(selectedZone)]
      : undefined;

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setGuides(response.data.guides || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener las guías",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchUnassingnedGuides = () => {
    setIsLoading(true);

    const fetchFunction = isSuperAdmin
      ? getUnassignedRoutesBySuperadmin
      : getUnassignedRoutes;
    const fetchParams = isSuperAdmin
      ? [Number(selectedZone)]
      : undefined;

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setUnassignedGuides(response.data.unassigned_routes || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener las guías",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchGuidesInIncidence = () => {
    setIsLoading(true);

    const fetchFunction = isSuperAdmin
      ? getGuidesInIncidenceBySuperadmin
      : getGuidesInIncidence;
    const fetchParams = isSuperAdmin
      ? [Number(selectedZone)]
      : undefined;

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setGuidesInIncidence(response.data.guides || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener las guías con incidencia",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchZones = () => {
    getZones()
      .then((response) => {
        if (response.data.status_Code === 200) {
          setZones(response.data.ls_zones || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener las zonas",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  const handleZoneChange = (event) => {
    const zoneValue = event.target.value;
    setSelectedZone(Number(zoneValue));
  };

  const fetchDeliverers = () => {
    const fetchFunction = isSuperAdmin
      ? getDeliveryMenBySuperadmin
      : getDeliveryMen;
    const fetchParams = isSuperAdmin
      ? undefined
      : { validatePermissions: "false", filterActive: "true" };

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setDeliverers(response.data.users || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener los repartidores",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  useEffect(() => {
    fetchZones();
  }, []);

  useEffect(() => {
    if (
      extraInformation?.id_role !== undefined &&
      !hasFetchedDeliverersInitial.current
    ) {
      hasFetchedDeliverersInitial.current = true;
      fetchDeliverers();
    }
  }, [extraInformation?.id_role]);

  useEffect(() => {
    if (extraInformation?.id_role !== undefined && !hasFetchedInitial.current) {
      hasFetchedInitial.current = true;

      if (hasAllPermissions(["rte_vw", "rte_pac_vw"])) {
        fetchGuides();
      }

      if (hasAllPermissions(["rte_vw", "rte_vwd_bat"])) {
        fetchUnassingnedGuides();
      }

      if (hasAllPermissions(["rte_vw", "rte_inc_vw"])) {
        fetchGuidesInIncidence();
      }
    }
  }, [extraInformation?.id_role]);

  useEffect(() => {
    if (
      isSuperAdmin &&
      extraInformation?.id_role === 5 &&
      hasFetchedInitial.current
    ) {
      if (hasAllPermissions(["rte_vw", "rte_pac_vw"])) {
        fetchGuides();
      }

      if (hasAllPermissions(["rte_vw", "rte_vwd_bat"])) {
        fetchUnassingnedGuides();
      }

      if (hasAllPermissions(["rte_vw", "rte_inc_vw"])) {
        fetchGuidesInIncidence();
      }
    }
  }, [selectedZone]);

  return (
    <Container style={styles.mainContainer}>
      <Title title="Rutas" />
      <SearchBar setSearch={setSearch}>
        {hasAllPermissions(["rte_asg", "rte_vw", "rte_pac_vw"]) && (
          <Button
            variant="contained"
            disabled={!hasItemsSelected}
            startIcon={<UserTag variant="Bold" />}
            onClick={() => handleDialogOpen("AssingUserGuides")}
            sx={hasItemsSelected ? styles.activeButton : styles.inactiveButton}
          >
            Asignar guías
          </Button>
        )}
      </SearchBar>

      {isSuperAdmin && (
        <Container style={{ marginBottom: "16px", marginTop: "16px" }}>
          <FormControl variant="standard" style={{ minWidth: 200 }}>
            <InputLabel id="zone-filter-label">Filtrar por zona</InputLabel>
            <Select
              labelId="zone-filter-label"
              id="zone-filter"
              value={selectedZone}
              onChange={handleZoneChange}
            >
              {zones.map((zone) => {
                const zoneId =
                  typeof zone === "string"
                    ? zone
                    : zone.id_zone || zone.id || zone.zone_code;
                const zoneName =
                  typeof zone === "string"
                    ? zone
                    : zone.zone_name || zone.name || zone.zone_code || zone.id;
                return (
                  <MenuItem key={zoneId} value={zoneId}>
                    {zoneName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Container>
      )}

      <GuidesTabBar
        guides={guides}
        unassignedGuides={unassignedGuides}
        guidesInIncidence={guidesInIncidence}
        fetchGuides={fetchGuides}
        fetchUnassingnedGuides={fetchUnassingnedGuides}
        isLoading={isLoading}
        search={search}
        setHasItemsSelected={setHasItemsSelected}
        selectedGuides={selectedGuides}
        setSelectedGuides={setSelectedGuides}
      />

      <GuidesAssingUserDialog
        open={openDialog === "AssingUserGuides"}
        fetchGuides={fetchGuides}
        fetchUnassingnedGuides={fetchUnassingnedGuides}
        onClose={handleDialogClose}
        selectedGuides={selectedGuides}
        deliverers={deliverers}
        selectedZone={selectedZone}
      />
    </Container>
  );
};

export default Guides;
